import * as THREE from 'three';

import type { IGltfDataParser, IGltfTreeNode } from './types';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

/** Utility to parse GLTF data into a tree structure */
export class GltfDataParser implements IGltfDataParser {
  private idCounter = 0;

  private generateId(): string {
    return `gltf-node-${++this.idCounter}`;
  }

  parseGltf(gltf: GLTF): IGltfTreeNode {
    // Reset counter for consistent parsing
    this.idCounter = 0;

    const rootNode: IGltfTreeNode = {
      id: this.generateId(),
      name: 'GLTF Asset',
      type: 'root',
      children: [],
    };

    // Add scene
    if (gltf.scene) {
      const sceneNode = this.parseScene(gltf.scene);
      sceneNode.parent = rootNode;
      rootNode.children.push(sceneNode);
    }

    // Add animations
    if (gltf.animations && gltf.animations.length > 0) {
      const animationsNode: IGltfTreeNode = {
        id: this.generateId(),
        name: `Animations (${gltf.animations.length})`,
        type: 'animation',
        children: [],
        parent: rootNode,
      };

      gltf.animations.forEach((animation, index) => {
        const animNode: IGltfTreeNode = {
          id: this.generateId(),
          name: animation.name || `Animation ${index}`,
          type: 'animation',
          children: [],
          data: animation,
          parent: animationsNode,
        };
        animationsNode.children.push(animNode);
      });

      rootNode.children.push(animationsNode);
    }

    // Add cameras
    if (gltf.cameras && gltf.cameras.length > 0) {
      const camerasNode: IGltfTreeNode = {
        id: this.generateId(),
        name: `Cameras (${gltf.cameras.length})`,
        type: 'camera',
        children: [],
        parent: rootNode,
      };

      gltf.cameras.forEach((camera, index) => {
        const camNode: IGltfTreeNode = {
          id: this.generateId(),
          name: camera.name || `Camera ${index}`,
          type: 'camera',
          children: [],
          data: camera,
          parent: camerasNode,
        };
        camerasNode.children.push(camNode);
      });

      rootNode.children.push(camerasNode);
    }

    return rootNode;
  }

  private parseScene(scene: THREE.Object3D): IGltfTreeNode {
    const sceneNode: IGltfTreeNode = {
      id: this.generateId(),
      name: scene.name || 'Scene',
      type: 'scene',
      children: [],
      data: scene,
    };

    // Parse all children in the scene
    scene.children.forEach(child => {
      const childNode = this.parseObject3D(child);
      childNode.parent = sceneNode;
      sceneNode.children.push(childNode);
    });

    return sceneNode;
  }

  private parseObject3D(object: THREE.Object3D): IGltfTreeNode {
    let nodeType: IGltfTreeNode['type'] = 'object3d';
    let name = object.name || object.type || 'Object3D';

    // Determine node type based on object type
    if (object instanceof THREE.Mesh) {
      nodeType = 'mesh';
      name = object.name || 'Mesh';
    } else if (object instanceof THREE.Light) {
      nodeType = 'light';
      name = object.name || `${object.type}`;
    } else if (object instanceof THREE.Camera) {
      nodeType = 'camera';
      name = object.name || `${object.type}`;
    }

    const node: IGltfTreeNode = {
      id: this.generateId(),
      name,
      type: nodeType,
      children: [],
      data: object,
    };

    // Add materials directly as children if this is a mesh
    if (object instanceof THREE.Mesh && object.material) {
      const materials = Array.isArray(object.material) ? object.material : [object.material];

      materials.forEach((material, index) => {
        const matNode = this.parseMaterial(material, index);
        matNode.parent = node;
        node.children.push(matNode);
      });
    }

    // Parse children recursively
    object.children.forEach(child => {
      const childNode = this.parseObject3D(child);
      childNode.parent = node;
      node.children.push(childNode);
    });

    return node;
  }

  private parseMaterial(material: THREE.Material, index: number): IGltfTreeNode {
    const materialNode: IGltfTreeNode = {
      id: this.generateId(),
      name: material.name || `Material_${index}`,
      type: 'material',
      children: [],
      data: material,
    };

    // Parse textures in the material
    const textures: { name: string; texture: THREE.Texture }[] = [];

    // Common texture properties to check
    const textureProperties = [
      { prop: 'map', name: 'Diffuse Map' },
      { prop: 'normalMap', name: 'Normal Map' },
      { prop: 'roughnessMap', name: 'Roughness Map' },
      { prop: 'metalnessMap', name: 'Metalness Map' },
      { prop: 'emissiveMap', name: 'Emissive Map' },
      { prop: 'aoMap', name: 'AO Map' },
      { prop: 'envMap', name: 'Environment Map' },
      { prop: 'lightMap', name: 'Light Map' },
      { prop: 'bumpMap', name: 'Bump Map' },
      { prop: 'displacementMap', name: 'Displacement Map' },
      { prop: 'alphaMap', name: 'Alpha Map' },
    ];

    textureProperties.forEach(({ prop, name }) => {
      const texture = (material as any)[prop];
      if (texture && texture instanceof THREE.Texture) {
        textures.push({ name, texture });
      }
    });

    // Add textures directly as children of the material
    textures.forEach(({ name, texture }) => {
      const texNode: IGltfTreeNode = {
        id: this.generateId(),
        name,
        type: 'texture',
        children: [],
        data: texture,
        parent: materialNode,
      };
      materialNode.children.push(texNode);
    });

    return materialNode;
  }
}

/** Singleton instance */
export const gltfDataParser = new GltfDataParser();